<template>
  <AppDialog
    v-model="dialog"
    size="sm"
    hide-close
    persistent
    content-class="login-dialog"
  >
    <!-- Cabecera propia: logo, bienvenida y cierre. -->
    <template #header>
      <div class="login-card__header">
        <v-btn
          icon
          variant="text"
          size="small"
          class="login-card__close"
          :aria-label="t('actions.close')"
          @click="handleClose"
        >
          <v-icon icon="mdi-close" size="20" />
        </v-btn>
        <div class="login-card__logo">
          <img src="/logo/watchstreak-logo.svg" alt="WatchStreak" class="login-card__logo-img" />
          <h2 class="login-card__title">{{ t('auth.welcome_title') }}</h2>
          <p v-if="!registerSuccess" class="login-card__subtitle">{{ t('auth.welcome_subtitle') }}</p>
        </div>
      </div>
    </template>

        <!-- Mensaje tras registro: activar cuenta por correo -->
        <div v-if="registerSuccess" class="register-success">
          <div class="register-success__icon-wrap">
            <v-icon icon="mdi-email" size="56" class="register-success__icon" />
          </div>
          <h3 class="register-success__title">{{ t('auth.check_inbox_title') }}</h3>
          <p class="register-success__text">
            {{ t('auth.check_inbox_text') }}
          </p>
          <v-btn
            variant="flat"
            size="large"
            class="btn-gradient-primary register-success__btn"
            @click="closeAfterRegister"
          >
            {{ t('actions.close') }}
          </v-btn>
        </div>

        <!-- Tabs: Iniciar Sesión / Registrarse -->
        <div v-else class="login-tabs">
          <button
            type="button"
            :class="['login-tabs__tab', activeTab === 'login' ? 'login-tabs__tab--active' : 'login-tabs__tab--inactive']"
            @click="activeTab = 'login'"
          >
            {{ t('auth.tab_login') }}
          </button>
          <button
            type="button"
            :class="['login-tabs__tab', activeTab === 'register' ? 'login-tabs__tab--active' : 'login-tabs__tab--inactive']"
            @click="activeTab = 'register'"
          >
            {{ t('auth.tab_register') }}
          </button>
        </div>

        <!-- Formulario de Login -->
        <form v-if="!registerSuccess && activeTab === 'login'" @submit.prevent="handleLogin" class="app-form mt-7">
          <div class="app-input-wrap mb-5">
            <label for="login-email" class="app-input-label">{{ t('auth.email_label') }}</label>
            <div class="app-input-inner" :class="{ 'has-error': errors.email.length }">
              <span class="app-input__icon" aria-hidden="true">
                <v-icon icon="mdi-email-outline" size="20" />
              </span>
              <input
                id="login-email"
                v-model="form.email"
                type="email"
                autocomplete="email"
                class="app-input"
                :placeholder="t('auth.placeholder_email')"
                required
                autofocus
              />
            </div>
            <p v-if="errors.email.length" class="app-input__error">{{ errors.email[0] }}</p>
          </div>

          <div class="app-input-wrap mb-5">
            <label for="login-password" class="app-input-label">{{ t('auth.password') }}</label>
            <div class="app-input-inner" :class="{ 'has-error': errors.password.length }">
              <span class="app-input__icon" aria-hidden="true">
                <v-icon icon="mdi-lock-outline" size="20" />
              </span>
              <input
                id="login-password"
                v-model="form.password"
                autocomplete="current-password"
                :type="showPassword ? 'text' : 'password'"
                class="app-input"
                required
              />
              <button
                type="button"
                class="app-input__append"
                :aria-label="showPassword ? t('auth.hide_password') : t('auth.show_password')"
                @click="showPassword = !showPassword"
              >
                <v-icon :icon="showPassword ? 'mdi-eye-off' : 'mdi-eye'" size="20" />
              </button>
            </div>
            <p v-if="errors.password.length" class="app-input__error">{{ errors.password[0] }}</p>
          </div>

          <div class="login-form__row mb-6">
            <v-checkbox
              v-model="form.remember"
              :label="t('auth.remember_me')"
              color="primary"
              hide-details
              density="comfortable"
              class="app-form-checkbox flex-grow-0"
            />
            <button
              type="button"
              class="login-form__forgot"
              :disabled="forgotLoading"
              @click="onForgotPassword"
            >
              {{ forgotLoading ? t('auth.forgot_password_sending') : t('auth.forgot_password') }}
            </button>
          </div>

          <v-alert
            v-if="errorMessage"
            type="error"
            variant="tonal"
            class="mb-6"
            closable
            density="comfortable"
            @click:close="errorMessage = ''"
          >
            {{ errorMessage }}
          </v-alert>

          <v-alert
            v-if="infoMessage"
            type="success"
            variant="tonal"
            class="mb-6"
            closable
            density="comfortable"
            @click:close="infoMessage = ''"
          >
            {{ infoMessage }}
          </v-alert>

          <v-btn
            type="submit"
            size="default"
            block
            class="btn-gradient-primary mb-6"
            :loading="loading"
            :disabled="loading"
          >
            {{ t('auth.login') }}
          </v-btn>

          <div class="login-form__divider mb-6">
            <span class="login-form__divider-text">{{ t('auth.continue_with') }}</span>
          </div>

          <div class="login-form__social">
            <v-btn
              variant="outlined"
              class="login-form__social-btn"
              :href="socialAuthUrl('google')"
              target="_self"
              rel="noopener"
            >
              <v-icon icon="mdi-google" start size="20" />
              Google
            </v-btn>
            <v-btn
              variant="outlined"
              class="login-form__social-btn"
              :href="socialAuthUrl('facebook')"
              target="_self"
              rel="noopener"
            >
              <v-icon icon="mdi-facebook" start size="20" />
              Facebook
            </v-btn>
          </div>
        </form>

        <!-- Formulario de Registro -->
        <form v-else-if="!registerSuccess" @submit.prevent="handleRegister" class="app-form mt-7">
          <div class="app-input-wrap mb-5">
            <label for="register-name" class="app-input-label">{{ t('auth.name') }}</label>
            <div class="app-input-inner" :class="{ 'has-error': errors.name?.length }">
              <span class="app-input__icon" aria-hidden="true">
                <v-icon icon="mdi-account-outline" size="20" />
              </span>
              <input
                id="register-name"
                v-model="registerForm.name"
                type="text"
                class="app-input"
                :placeholder="t('auth.placeholder_name')"
                required
                autocomplete="name"
              />
            </div>
            <p v-if="errors.name?.length" class="app-input__error">{{ errors.name[0] }}</p>
          </div>

          <div class="app-input-wrap mb-5">
            <label for="register-email" class="app-input-label">{{ t('auth.email_label') }}</label>
            <div class="app-input-inner" :class="{ 'has-error': errors.email?.length }">
              <span class="app-input__icon" aria-hidden="true">
                <v-icon icon="mdi-email-outline" size="20" />
              </span>
              <input
                id="register-email"
                v-model="registerForm.email"
                type="email"
                class="app-input"
                :placeholder="t('auth.placeholder_email')"
                required
                autocomplete="email"
              />
            </div>
            <p v-if="errors.email?.length" class="app-input__error">{{ errors.email[0] }}</p>
          </div>

          <div class="app-input-wrap mb-5">
            <label for="register-password" class="app-input-label">{{ t('auth.password') }}</label>
            <div class="app-input-inner" :class="{ 'has-error': errors.password?.length }">
              <span class="app-input__icon" aria-hidden="true">
                <v-icon icon="mdi-lock-outline" size="20" />
              </span>
              <input
                id="register-password"
                v-model="registerForm.password"
                :type="showRegisterPassword ? 'text' : 'password'"
                class="app-input"
                :placeholder="t('auth.password_placeholder_register')"
                required
                autocomplete="new-password"
              />
              <button
                type="button"
                class="app-input__append"
                :aria-label="showRegisterPassword ? t('auth.hide_password') : t('auth.show_password')"
                @click="showRegisterPassword = !showRegisterPassword"
              >
                <v-icon :icon="showRegisterPassword ? 'mdi-eye-off' : 'mdi-eye'" size="20" />
              </button>
            </div>
            <p v-if="errors.password?.length" class="app-input__error">{{ errors.password[0] }}</p>
            <p v-else class="app-input__hint mt-1">{{ t('auth.password_hint') }}</p>
          </div>

          <div class="app-input-wrap mb-6">
            <label for="register-password-confirm" class="app-input-label">{{ t('auth.confirm_password') }}</label>
            <div class="app-input-inner" :class="{ 'has-error': errors.password_confirmation?.length }">
              <span class="app-input__icon" aria-hidden="true">
                <v-icon icon="mdi-lock-check-outline" size="20" />
              </span>
              <input
                id="register-password-confirm"
                v-model="registerForm.password_confirmation"
                type="password"
                class="app-input"
                :placeholder="t('auth.placeholder_confirm_password')"
                required
                autocomplete="new-password"
              />
            </div>
            <p v-if="errors.password_confirmation?.length" class="app-input__error">{{ errors.password_confirmation[0] }}</p>
          </div>

          <v-alert
            v-if="errorMessage"
            type="error"
            variant="tonal"
            class="mb-6"
            closable
            density="comfortable"
            @click:close="errorMessage = ''"
          >
            {{ errorMessage }}
          </v-alert>

          <v-btn
            type="submit"
            size="default"
            block
            class="btn-gradient-primary mb-6"
            :loading="loading"
            :disabled="loading"
          >
            {{ t('auth.create_account') }}
          </v-btn>

          <div class="login-form__divider mb-6">
            <span class="login-form__divider-text">{{ t('auth.continue_with') }}</span>
          </div>

          <div class="login-form__social">
            <v-btn
              variant="outlined"
              class="login-form__social-btn"
              :href="socialAuthUrl('google')"
              target="_self"
              rel="noopener"
            >
              <v-icon icon="mdi-google" start size="20" />
              Google
            </v-btn>
            <v-btn
              variant="outlined"
              class="login-form__social-btn"
              :href="socialAuthUrl('facebook')"
              target="_self"
              rel="noopener"
            >
              <v-icon icon="mdi-facebook" start size="20" />
              Facebook
            </v-btn>
          </div>
        </form>
  </AppDialog>
</template>

<script setup>
import { ref, reactive, watch, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useAuthStore } from '@/stores/auth'
import { API_BASE_URL, apiFetch } from '@/utils/api'
import AppDialog from '@/components/ui/AppDialog.vue'

const { t } = useI18n()

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['update:modelValue', 'success'])

const authStore = useAuthStore()
const registerSuccess = ref(false)

const dialog = ref(props.modelValue)
const activeTab = ref('login')
const showPassword = ref(false)
const showRegisterPassword = ref(false)
const form = reactive({
  email: '',
  password: '',
  remember: false,
})

const registerForm = reactive({
  name: '',
  email: '',
  password: '',
  password_confirmation: '',
})

const errors = reactive({
  name: [],
  email: [],
  password: [],
  password_confirmation: [],
})

const loading = computed(() => authStore.loading)
const errorMessage = ref('')
const infoMessage = ref('')
const forgotLoading = ref(false)

watch(() => props.modelValue, (newVal) => {
  dialog.value = newVal
  if (newVal) {
    activeTab.value = 'login'
    registerSuccess.value = false
    form.email = ''
    form.password = ''
    form.remember = false
    registerForm.name = ''
    registerForm.email = ''
    registerForm.password = ''
    registerForm.password_confirmation = ''
    errors.name = []
    errors.email = []
    errors.password = []
    errors.password_confirmation = []
    errorMessage.value = ''
  }
})

watch(dialog, (newVal) => {
  emit('update:modelValue', newVal)
})

/**
 * Pide el enlace de restablecimiento. El endpoint y el correo ya existían; lo
 * único que hacía este botón era pintar "próximamente" como error rojo.
 *
 * El backend responde 200 siempre, exista o no la cuenta, para no revelar qué
 * correos están registrados; el mensaje que se muestra respeta esa ambigüedad.
 */
async function onForgotPassword() {
  errorMessage.value = ''
  infoMessage.value = ''

  const email = form.email.trim()
  if (!email) {
    errorMessage.value = t('auth.forgot_password_need_email')
    return
  }

  forgotLoading.value = true
  try {
    const res = await apiFetch('/api/v1/auth/forgot-password', {
      method: 'POST',
      body: JSON.stringify({ email }),
    })
    if (!res.ok) {
      const data = await res.json().catch(() => ({}))
      errorMessage.value = data.errors?.[0]?.detail || t('auth.forgot_password_failed')
      return
    }
    infoMessage.value = t('auth.forgot_password_sent')
  } catch {
    errorMessage.value = t('auth.forgot_password_failed')
  } finally {
    forgotLoading.value = false
  }
}

function socialAuthUrl(provider) {
  return `${API_BASE_URL}/api/v1/auth/${provider}`
}

const PASSWORD_REGEX = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/

function validateRegisterForm() {
  errors.name = []
  errors.email = []
  errors.password = []
  errors.password_confirmation = []
  let valid = true

  if (!registerForm.name?.trim()) {
    errors.name.push(t('auth.err_name_required'))
    valid = false
  }

  if (!registerForm.email?.trim()) {
    errors.email.push(t('auth.err_email_required'))
    valid = false
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(registerForm.email)) {
    errors.email.push(t('auth.err_email_invalid'))
    valid = false
  }

  if (!registerForm.password) {
    errors.password.push(t('auth.err_password_required'))
    valid = false
  } else if (!PASSWORD_REGEX.test(registerForm.password)) {
    errors.password.push(t('auth.err_password_rules'))
    valid = false
  }

  if (registerForm.password !== registerForm.password_confirmation) {
    errors.password_confirmation.push(t('auth.err_passwords_mismatch'))
    valid = false
  }

  return valid
}

async function handleLogin() {
  errors.email = []
  errors.password = []
  errorMessage.value = ''

  const result = await authStore.login(form.email, form.password, form.remember)

  if (result.success) {
    dialog.value = false
    emit('success')
  } else {
    if (result.errors && Array.isArray(result.errors)) {
      result.errors.forEach((error) => {
        if (error.detail) {
          if (error.detail.includes('email') || error.detail.toLowerCase().includes('email')) {
            errors.email.push(error.detail)
          } else if (error.detail.includes('password') || error.detail.includes('credenciales')) {
            errors.password.push(error.detail)
          } else {
            errorMessage.value = error.detail
          }
        }
      })
    } else {
      errorMessage.value = result.message || t('auth.login_failed')
    }
  }
}

async function handleRegister() {
  errorMessage.value = ''
  if (!validateRegisterForm()) return

  const result = await authStore.register(
    registerForm.name.trim(),
    registerForm.email.trim(),
    registerForm.password,
    registerForm.password_confirmation
  )

  if (result.success) {
    registerSuccess.value = true
  } else {
    if (result.errors && Array.isArray(result.errors)) {
      result.errors.forEach((error) => {
        if (error.detail) {
          const field = error.source?.pointer?.match(/attributes\/(\w+)/)?.[1]
          if (field && errors[field]) {
            errors[field].push(error.detail)
          } else {
            errorMessage.value = error.detail
          }
        }
      })
    } else {
      errorMessage.value = result.message || t('auth.register_failed')
    }
  }
}

function handleClose() {
  registerSuccess.value = false
  dialog.value = false
  emit('success')
}

function closeAfterRegister() {
  handleClose()
}
</script>

<style scoped>
/* Sobrescribir border-radius 4px que aplica Vuetify a .v-dialog > .v-overlay__content > .v-card */
.login-dialog :deep(.v-overlay__content) {
  border-radius: 36px !important;
  overflow: hidden;
}

.login-dialog :deep(.v-overlay__content > .v-card),
.login-dialog :deep(.v-overlay__content > form > .v-card) {
  border-radius: 36px !important;
}

.login-card {
  background: rgb(18, 18, 18) !important;
  border-radius: 18px !important;
  border: 1px solid rgba(255, 255, 255, 0.06);
  overflow: hidden;
}

.login-card__header {
  position: relative;
  padding: 32px 28px 24px;
  text-align: center;
}

.login-card__close {
  position: absolute;
  top: 12px;
  right: 12px;
  color: rgba(255, 255, 255, 0.6) !important;
}

.login-card__close:hover {
  color: rgba(255, 255, 255, 0.9) !important;
}

/* El logo ya trae su fondo con degradado: aquí solo tamaño y centrado. */
.login-card__logo-img {
  width: 64px;
  height: 64px;
  border-radius: 16px;
  display: block;
  margin: 0 auto 20px;
}

.login-card__title {
  font-size: 1.5rem;
  font-weight: 700;
  color: #fff;
  margin: 0 0 10px;
  line-height: 1.25;
}

.login-card__subtitle {
  font-size: 0.875rem;
  color: rgba(255, 255, 255, 0.65);
  margin: 0;
  line-height: 1.35;
}

.login-card__body {
  padding: 0 28px 32px !important;
  color: rgba(255, 255, 255, 0.9);
}

/* Pantalla de éxito tras registro: activar cuenta por correo */
.register-success {
  text-align: center;
  padding: 24px 0 32px;
}
.register-success__icon-wrap {
  width: 96px;
  height: 96px;
  margin: 0 auto 24px;
  border-radius: 50%;
  background: linear-gradient(135deg, rgba(219, 39, 119, 0.4) 0%, rgba(236, 72, 153, 0.35) 100%);
  display: flex;
  align-items: center;
  justify-content: center;
}
.register-success__icon-wrap :deep(.v-icon),
.register-success__icon {
  color: rgb(244, 114, 182) !important;
  opacity: 1;
}
.register-success__icon-wrap :deep(svg) {
  fill: currentColor;
}
.register-success__title {
  font-size: 1.25rem;
  font-weight: 600;
  color: #fff;
  margin: 0 0 12px;
  line-height: 1.3;
}
.register-success__text {
  font-size: 0.9375rem;
  color: rgba(255, 255, 255, 0.75);
  margin: 0 auto 28px;
  line-height: 1.5;
  max-width: 320px;
}
.register-success__btn {
  text-transform: none;
  font-weight: 600;
  min-width: 140px;
}

/* Tabs: muy redondeados como en la referencia */
.login-tabs {
  display: flex;
  gap: 10px;
  border-radius: 14px;
  padding: 6px;
  background: rgba(255, 255, 255, 0.06);
}

.login-tabs__tab {
  flex: 1;
  padding: 8px 16px;
  min-height: 36px;
  max-height: 36px;
  border: none;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s, color 0.2s;
}

.login-tabs__tab--active {
  background: linear-gradient(135deg, var(--color-purple-600) 0%, var(--color-pink-600) 100%);
  color: #fff;
}

.login-tabs__tab--inactive {
  background: transparent;
  color: rgba(255, 255, 255, 0.5);
  cursor: pointer;
}

/* Form row: checkbox + forgot link */
.login-form__row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
}

.login-form__forgot {
  font-size: 14px;
  color: oklch(0.714 0.203 305.504);
  text-decoration: none;
  background: transparent;
}

.login-form__forgot:hover {
  color: oklch(0.827 0.119 306.383);
  background: transparent !important;
}

/* Divider: más espacio alrededor */
.login-form__divider {
  display: flex;
  align-items: center;
  gap: 16px;
}

.login-form__divider::before,
.login-form__divider::after {
  content: '';
  flex: 1;
  height: 1px;
  background: rgba(255, 255, 255, 0.2);
}

.login-form__divider-text {
  font-size: 14px;
  color: rgba(255, 255, 255, 0.6);
}

/* Social buttons: redondeados como en la referencia */
.login-form__social {
  display: flex;
  gap: 14px;
}

.login-form__social-btn {
  flex: 1;
  min-height: 36px !important;
  max-height: 36px !important;
  font-size: 14px !important;
  border-radius: 12px !important;
  border-color: rgba(255, 255, 255, 0.25) !important;
  color: rgba(255, 255, 255, 0.85) !important;
  text-transform: none !important;
}

.login-form__social-btn:disabled {
  opacity: 0.7;
}

.login-card :deep(.v-checkbox .v-label) {
  color: var(--color-form-text) !important;
  font-size: 14px !important;
}

.login-card :deep(.v-alert) {
  border-radius: 12px;
}

.app-input__hint {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.5);
  margin: 0;
}
</style>
